import React, { useState } from 'react';
import { X, Mail, Lock, KeyRound, AlertCircle } from 'lucide-react';
import { Button } from '../../../ui/Button';
import { LinkedInIcon } from '../../../ui/LinkedInIcon';
import { apiClient } from '../../../../lib/api';

interface LinkedInConnectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialEmail?: string;
}

type Step = 'login' | 'verify' | 'success';

export const LinkedInConnectModal: React.FC<LinkedInConnectModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialEmail = '',
}) => {
  const [step, setStep] = useState<Step>('login');
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setStep('login');
    setEmail(initialEmail);
    setPassword('');
    setVerificationCode('');
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await apiClient.loginLinkedIn(email, password);

      if (response.status === 'verification_required' || response.message?.toLowerCase().includes('verification code')) {
        setStep('verify');
      } else if (response.login_token) {
        setStep('success');
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 1500);
      } else {
        setError(response.message || 'Login failed. Please try again.');
      }
    } catch (err: any) {
      const status = err?.response?.status;
      // 502 timeout likely means verification email was sent - proceed to verify step
      if (status === 502) {
        setStep('verify');
      } else {
        const message = err?.response?.data?.detail || 'Failed to connect. Please try again.';
        setError(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await apiClient.verifyLinkedInCode(email, verificationCode);

      if (response.login_token) {
        setStep('success');
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 1500);
      } else {
        setError(response.message || 'Verification failed. Please try again.');
      }
    } catch (err: any) {
      const message = err?.response?.data?.detail || 'Verification failed. Please try again.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="linkedin-connect-modal-title"
        className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-linkedin flex items-center justify-center">
              <LinkedInIcon size={16} className="text-white" />
            </div>
            <span id="linkedin-connect-modal-title" className="text-sm font-semibold text-neutral-900">
              Connect LinkedIn
            </span>
          </div>
          <button
            onClick={handleClose}
            aria-label="Close dialog"
            className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-400"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {step === 'success' ? (
            <SuccessState />
          ) : step === 'verify' ? (
            <VerifyForm
              email={email}
              code={verificationCode}
              onCodeChange={setVerificationCode}
              onSubmit={handleVerify}
              onBack={() => setStep('login')}
              isLoading={isLoading}
              error={error}
            />
          ) : (
            <LoginForm
              email={email}
              password={password}
              onEmailChange={setEmail}
              onPasswordChange={setPassword}
              onSubmit={handleLogin}
              isLoading={isLoading}
              error={error}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const LoginForm: React.FC<{
  email: string;
  password: string;
  onEmailChange: (v: string) => void;
  onPasswordChange: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  isLoading: boolean;
  error: string | null;
}> = ({ email, password, onEmailChange, onPasswordChange, onSubmit, isLoading, error }) => (
  <form onSubmit={onSubmit} className="space-y-4">
    <p className="text-xs text-neutral-500 mb-4">
      Enter your LinkedIn credentials to connect your account for outreach.
    </p>

    {error && <ErrorMessage message={error} />}

    <div>
      <label htmlFor="linkedin-email" className="block text-xs font-medium text-neutral-700 mb-1">
        LinkedIn Email
      </label>
      <div className="relative">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
        <input
          id="linkedin-email"
          type="email"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          placeholder="you@example.com"
          className="w-full pl-10 pr-3 py-2 text-13 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-linkedin/20 focus:border-linkedin"
          required
          disabled={isLoading}
        />
      </div>
    </div>

    <div>
      <label htmlFor="linkedin-password" className="block text-xs font-medium text-neutral-700 mb-1">
        Password
      </label>
      <div className="relative">
        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
        <input
          id="linkedin-password"
          type="password"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          placeholder="••••••••"
          className="w-full pl-10 pr-3 py-2 text-13 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-linkedin/20 focus:border-linkedin"
          required
          disabled={isLoading}
        />
      </div>
    </div>

    <Button
      type="submit"
      variant="primary"
      className="w-full bg-linkedin hover:bg-linkedin-hover"
      disabled={isLoading || !email || !password}
      loading={isLoading}
    >
      {isLoading ? 'Connecting...' : 'Connect LinkedIn'}
    </Button>

    <p className="text-2xs text-neutral-400 text-center">
      Your credentials are securely transmitted and not stored in plain text.
    </p>
  </form>
);

const VerifyForm: React.FC<{
  email: string;
  code: string;
  onCodeChange: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onBack: () => void;
  isLoading: boolean;
  error: string | null;
}> = ({ email, code, onCodeChange, onSubmit, onBack, isLoading, error }) => (
  <form onSubmit={onSubmit} className="space-y-4">
    <div className="text-center mb-4">
      <div className="w-12 h-12 rounded-full bg-linkedin/10 flex items-center justify-center mx-auto mb-3">
        <KeyRound className="w-6 h-6 text-linkedin" />
      </div>
      <h3 className="text-sm font-semibold text-neutral-900 mb-1">
        Verification Required
      </h3>
      <p className="text-xs text-neutral-500">
        LinkedIn sent a verification code to <strong>{email}</strong>
      </p>
    </div>

    {error && <ErrorMessage message={error} />}

    <div>
      <label htmlFor="linkedin-verification-code" className="block text-xs font-medium text-neutral-700 mb-1">
        Verification Code
      </label>
      <input
        id="linkedin-verification-code"
        type="text"
        value={code}
        onChange={(e) => onCodeChange(e.target.value)}
        placeholder="Enter 6-digit code"
        className="w-full px-3 py-2 text-13 text-center tracking-widest border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-linkedin/20 focus:border-linkedin"
        required
        disabled={isLoading}
        maxLength={6}
      />
    </div>

    <div className="flex gap-2">
      <Button
        type="button"
        variant="secondary"
        onClick={onBack}
        disabled={isLoading}
        className="flex-1"
      >
        Back
      </Button>
      <Button
        type="submit"
        variant="primary"
        className="flex-1 bg-linkedin hover:bg-linkedin-hover"
        disabled={isLoading || code.length < 4}
        loading={isLoading}
      >
        Verify
      </Button>
    </div>
  </form>
);

const SuccessState: React.FC = () => (
  <div className="text-center py-4">
    <div className="w-12 h-12 rounded-full bg-success-100 flex items-center justify-center mx-auto mb-3">
      <LinkedInIcon size={24} className="text-success-600" />
    </div>
    <h3 className="text-sm font-semibold text-neutral-900 mb-1">
      LinkedIn Connected!
    </h3>
    <p className="text-xs text-neutral-500">
      Your account has been successfully connected.
    </p>
  </div>
);

const ErrorMessage: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex items-start gap-2 p-3 bg-error-50 border border-error-100 rounded-lg">
    <AlertCircle className="w-4 h-4 text-error-500 flex-shrink-0 mt-0.5" />
    <span className="text-xs text-error-600">{message}</span>
  </div>
);
